import { AnimatePresence, motion } from 'framer-motion';
import { FloatingPortal } from 'library/floating';
import { ChevronDownIcon, CrossIcon, ExclamationCircleFilledIcon } from 'library/icons';
import { FC, useCallback, useState } from 'react';
import { useProgressTabContext } from '../context/progress-tab-context';
import { collapseAnimation, modalAnimation } from '../helpers/progress-tab.animations';
import { IProgressTabItem } from '../helpers/progress-tab.types';
import ProgressFileIcon from './ProgressFileIcon';
import EventBlocker from 'modules/shared/components/helpers/EventBlocker';
import LinearProgress from 'library/loaders/components/LinearProgress';

interface ProgressTabModalProps {}

const ProgressTabModal: FC<ProgressTabModalProps> = () => {
    const { items, removeItem, reset } = useProgressTabContext();

    const [isExpanded, setIsExpanded] = useState<boolean>(true);

    const handleCancel = useCallback(
        (item: IProgressTabItem) => {
            if (item.isCancellable && !(item.isCompleted || item.isErrored)) {
                item?.onCancel();
            }

            item.onDismiss?.();
            removeItem(item._id);
        },
        [removeItem]
    );

    const handleClick = useCallback((item: IProgressTabItem) => {
        if (!item.isCompleted) return;

        item?.onClick();
    }, []);

    const showCrossButton = useCallback((item: IProgressTabItem) => {
        return item.isCancellable || item.isCompleted || item.isErrored;
    }, []);

    const progressColor = useCallback((item: IProgressTabItem) => {
        if (item.isCompleted) return 'success';
        else if (item.isErrored) return 'danger';
        else return 'primary';
    }, []);

    const handleCloseClick = useCallback(() => {
        items.forEach((item) => item.onDismiss?.());
        reset();

        setIsExpanded(true);
    }, [items, reset]);

    return (
        <FloatingPortal root={document.getElementById('modal-root')}>
            <AnimatePresence initial={false} mode="wait">
                {items.length > 0 && (
                    <motion.div
                        {...modalAnimation}
                        className="fixed right-10 bottom-10 w-[346px] overflow-y-auto z-[70] rounded-[10px] bg-floating-color border border-[#F4F5F7] dark:border-grayscale-50 shadow-[0px_2px_32px_-4px_rgba(3,17,22,0.1)] dark:shadow-[0px_2px_32px_-4px_rgba(3,17,22,0.8)] outline-none"
                    >
                        <div className="flex items-center p-4">
                            <div className="text-sm font-medium">Progress Tab</div>

                            <div className="ml-auto flex items-center gap-2">
                                <button
                                    className="grid place-items-center w-4 h-4 text-grayscale-500 rotate-180"
                                    aria-expanded={isExpanded}
                                    onClick={() => setIsExpanded((prev) => !prev)}
                                >
                                    <ChevronDownIcon className="collapse-arrow" />
                                </button>
                                <button className="text-xs text-grayscale-500 font-medium" onClick={handleCloseClick}>
                                    Close
                                </button>
                            </div>
                        </div>

                        <AnimatePresence initial={false} mode="wait">
                            {isExpanded && (
                                <motion.div
                                    {...collapseAnimation}
                                    className="divide-y divide-separator-color-light border-t border-separator-color-light overflow-hidden overflow-y-auto max-h-[75vh]"
                                >
                                    {items.map((item) => (
                                        <div
                                            key={item._id}
                                            className={`${item.isCompleted ? 'cursor-pointer' : ''}  p-4`}
                                            onClick={() => handleClick(item)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <ProgressFileIcon className="w-10" extension={item.extension} />
                                                </div>

                                                <div className="w-full overflow-hidden">
                                                    <div className="text-sm font-semibold overflow-hidden whitespace-nowrap text-ellipsis">
                                                        {item.title}
                                                    </div>
                                                    <div className="text-xs text-grayscale-500 mt-1">{item.text}</div>
                                                </div>

                                                {showCrossButton(item) && (
                                                    <EventBlocker stopPropagation>
                                                        <button className="ml-auto" onClick={() => handleCancel(item)}>
                                                            <CrossIcon
                                                                className="text-grayscale-500"
                                                                width="10"
                                                                height="10"
                                                            />
                                                        </button>
                                                    </EventBlocker>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3 mt-2">
                                                {/* Empty div  */}
                                                <div>
                                                    <div className="w-10" />
                                                </div>

                                                <LinearProgress
                                                    variant={item.indeterminate ? 'indeterminate' : 'determinate'}
                                                    className="w-full"
                                                    progress={item.progress}
                                                    color={progressColor(item)}
                                                />

                                                {item.isErrored ? (
                                                    <ExclamationCircleFilledIcon className="text-danger-500" />
                                                ) : (
                                                    <div className="text-xs text-grayscale-500 h-4">
                                                        {item.indeterminate ? '' : `${item.progress}%`}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </motion.div>
                )}
            </AnimatePresence>
        </FloatingPortal>
    );
};

export default ProgressTabModal;
